import React, { useEffect, useState } from 'react';
import { Check, Circle, ChevronRight, X, Sparkles, Upload, Package, MapPin, Tag } from 'lucide-react';
import { Link } from 'react-router-dom';
import { SupplierAPI, API_BASE } from '../../lib/api';

/**
 * Widget "Premiers pas" affiché au fournisseur tant qu'il n'a pas
 * complété son onboarding (profil + 1er produit + frais livraison + 1ère offre).
 *
 * S'auto-masque dès que les 4 étapes sont validées, ou si l'utilisateur ferme.
 * Persistant via localStorage : `dz_onboarding_dismissed_<supplier_id>`.
 */
const STEPS_DEF = [
  {
    id: 'profile',
    icon: MapPin,
    title: 'Complétez votre profil',
    desc: "Vérifiez votre nom, société, ville et téléphone dans l'onglet Paramètres.",
    cta: 'Aller aux Paramètres',
    test: (data) => Boolean(data.me?.name && data.me?.city),
  },
  {
    id: 'product',
    icon: Package,
    title: 'Ajoutez votre 1er produit',
    desc: "Saisissez manuellement ou importez tout votre catalogue via CSV en une fois.",
    cta: 'Nouveau produit',
    test: (data) => (data.products?.length || 0) > 0,
  },
  {
    id: 'shipping',
    icon: Upload,
    title: 'Configurez vos frais de livraison',
    desc: 'Définissez votre tarif livraison + seuil de gratuité dans Paramètres → Livraison.',
    cta: 'Configurer la livraison',
    test: (data) => Boolean(data.me?.shipping_fee != null || data.me?.free_shipping_threshold != null),
  },
  {
    id: 'offer',
    icon: Tag,
    title: 'Vérifiez vos offres',
    desc: "Au moins une variante avec prix > 0 pour apparaître dans le comparateur dentiste.",
    cta: 'Voir mes produits',
    test: (data) => (data.products || []).some(p => (p.variants || []).some(v => v.price > 0)),
  },
];

const OnboardingChecklist = ({ supplierId, onStepClick }) => {
  const [me, setMe] = useState(null);
  const [products, setProducts] = useState([]);
  const [dismissed, setDismissed] = useState(false);
  const [loading, setLoading] = useState(true);

  const dismissKey = `dz_onboarding_dismissed_${supplierId || 'anon'}`;

  useEffect(() => {
    try {
      if (localStorage.getItem(dismissKey) === '1') {
        setDismissed(true);
      }
    } catch { /* localStorage disabled — show the checklist anyway */ }

    Promise.all([
      SupplierAPI.me().catch(() => null),
      SupplierAPI.products().catch(() => []),
    ]).then(([m, p]) => {
      setMe(m);
      setProducts(Array.isArray(p) ? p : []);
      setLoading(false);
    });
  }, [dismissKey]);

  if (dismissed || loading) return null;

  const data = { me, products };
  const steps = STEPS_DEF.map(s => ({ ...s, done: s.test(data) }));
  const doneCount = steps.filter(s => s.done).length;
  const allDone = doneCount === steps.length;
  const percent = Math.round((doneCount / steps.length) * 100);

  // Auto-hide when 100% done
  if (allDone) {
    try { localStorage.setItem(dismissKey, '1'); } catch { /* noop */ }
    return null;
  }

  const dismiss = () => {
    try { localStorage.setItem(dismissKey, '1'); } catch { /* noop */ }
    setDismissed(true);
  };

  return (
    <div data-testid="supplier-onboarding-checklist"
      className="mb-6 bg-gradient-to-br from-[#1F4D3F] to-[#173B30] rounded-2xl p-5 text-white shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 flex-1">
          <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-5 h-5 text-[#E0A52E]" />
          </div>
          <div className="flex-1">
            <h3 className="font-serif text-lg font-semibold">Bienvenue · Premiers pas</h3>
            <p className="text-sm text-[#A8C5B5] mt-0.5">
              Complétez ces 4 étapes pour activer votre compte fournisseur. Vos produits apparaîtront ensuite dans le comparateur.
            </p>
          </div>
        </div>
        <button onClick={dismiss} title="Masquer"
          data-testid="onboarding-dismiss"
          className="text-[#A8C5B5] hover:text-white">
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Progress bar */}
      <div className="mt-4 flex items-center gap-3">
        <div className="flex-1 h-2 bg-white/10 rounded-full overflow-hidden">
          <div className="h-full bg-[#E0A52E] transition-all" style={{ width: `${percent}%` }} />
        </div>
        <span className="text-xs font-medium text-[#A8C5B5] whitespace-nowrap">{doneCount} / {steps.length} · {percent}%</span>
      </div>

      {/* Steps */}
      <ul className="mt-4 space-y-2">
        {steps.map((s) => {
          const Icon = s.icon;
          return (
            <li key={s.id}
              data-testid={`onboarding-step-${s.id}`}
              className={`flex items-start gap-3 rounded-lg p-2.5 transition-colors ${s.done ? 'bg-white/5' : 'bg-white/10 hover:bg-white/15 cursor-pointer'}`}
              onClick={() => !s.done && onStepClick?.(s.id)}>
              <div className="flex-shrink-0 mt-0.5">
                {s.done
                  ? <div className="w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center"><Check className="w-3 h-3 text-white" /></div>
                  : <Circle className="w-5 h-5 text-[#A8C5B5]" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className={`text-sm font-medium flex items-center gap-1.5 ${s.done ? 'text-[#A8C5B5] line-through' : 'text-white'}`}>
                  <Icon className="w-3.5 h-3.5" /> {s.title}
                </div>
                {!s.done && <div className="text-xs text-[#A8C5B5] mt-0.5">{s.desc}</div>}
              </div>
              {!s.done && (
                <button
                  type="button"
                  data-testid={`onboarding-cta-${s.id}`}
                  onClick={(e) => { e.stopPropagation(); onStepClick?.(s.id); }}
                  className="flex-shrink-0 text-xs font-medium text-[#E0A52E] hover:text-white inline-flex items-center gap-1">
                  {s.cta} <ChevronRight className="w-3 h-3" />
                </button>
              )}
            </li>
          );
        })}
      </ul>

      {/* CSV tip */}
      <div className="mt-3 pt-3 border-t border-white/10 text-xs text-[#A8C5B5]">
        💡 <strong className="text-white">Astuce :</strong> Importez tout votre catalogue en une seule fois via{' '}
        <Link to="/fournisseur" className="text-[#E0A52E] underline">Outils en masse → Import CSV</Link>
        {' '}— <a href={`${API_BASE}/public/supplier-csv-template`} download className="text-[#E0A52E] underline" data-testid="onboarding-csv-template">téléchargez le modèle</a>.
      </div>
    </div>
  );
};

export default OnboardingChecklist;
